import { useEffect, useSyncExternalStore } from 'react';
import { tokenStore } from '@/auth/token-store';
import { readJson, removeStorageKeys, writeStorage } from '@/lib/storage';

/**
 * Recently opened records, per tenant environment, for the command palette.
 * They name customers and documents, so sign-out clears them for every
 * tenant; switching tenant or environment keeps each scope's own list.
 */
export interface RecentItem {
  kind: string;
  id: string;
  title: string;
  subtitle?: string;
  path: string;
}

const LIMIT = 8;
const listeners = new Set<() => void>();
let cacheKey = '';
let cache: RecentItem[] = [];

function storageKey(): string {
  return `${PREFIX}${tokenStore.scopeId()}`;
}

function isRecentList(value: unknown): value is RecentItem[] {
  return (
    Array.isArray(value) &&
    value.every(
      (v) =>
        typeof v === 'object' &&
        v !== null &&
        typeof (v as RecentItem).path === 'string' &&
        typeof (v as RecentItem).title === 'string',
    )
  );
}

function read(): RecentItem[] {
  const key = storageKey();
  if (key !== cacheKey) {
    cacheKey = key;
    cache = readJson('local', key, isRecentList) ?? [];
  }
  return cache;
}

const PREFIX = 'biller.recent.';

/** Forget every scope's recent items (sign-out). */
export function clearRecent(): void {
  removeStorageKeys('local', PREFIX);
  cacheKey = '';
  cache = [];
  for (const listener of listeners) listener();
}

export function recordRecent(item: RecentItem): void {
  const next = [item, ...read().filter((r) => r.path !== item.path)].slice(0, LIMIT);
  cache = next;
  writeStorage('local', storageKey(), JSON.stringify(next));
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const unsubscribeToken = tokenStore.subscribe(listener);
  return () => {
    listeners.delete(listener);
    unsubscribeToken();
  };
}

export function useRecent(): RecentItem[] {
  return useSyncExternalStore(subscribe, read, read);
}

/** Record a detail page visit once its title is known. */
export function useRecordRecent(item: RecentItem | null): void {
  const key = item ? `${item.path}|${item.title}|${item.subtitle ?? ''}` : '';
  useEffect(() => {
    if (item) recordRecent(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by content
  }, [key]);
}
